import {
    getPwaBrandConfig,
    IPwaBrandConfig,
    PWA_BRAND_MESSAGE_TYPE,
    PWA_SERVICE_WORKER_URL,
} from '../constants';
import { IRegisterServiceWorkerResult } from '../types';

export interface IRegisterServiceWorkerOptions {
    /**
     * When false, registration is skipped (used in tests / local opt-out).
     * Defaults to production-only.
     */
    enabled?: boolean;
    swUrl?: string;
    registrationFn?: (scriptURL: string, options?: RegistrationOptions) => Promise<ServiceWorkerRegistration>;
    brand?: IPwaBrandConfig;
    /**
     * Called once a new service worker has taken over an already-running
     * session (i.e. not the very first activation for a fresh visitor). The
     * page is still showing the old bundle at this point — the caller decides
     * how/when to reload.
     */
    onUpdateAvailable?: () => void;
}

function postBrandToWorker(worker: ServiceWorker | null | undefined, brand: IPwaBrandConfig): void {
    if (!worker) {
        return;
    }
    worker.postMessage({ type: PWA_BRAND_MESSAGE_TYPE, brand });
}

/**
 * Push brand name / colors / icon URLs from Config into every SW state
 * so push notifications never fall back to a hardcoded white-label name.
 */
export function syncPwaBrandToServiceWorker(
    registration: ServiceWorkerRegistration,
    brand: IPwaBrandConfig = getPwaBrandConfig()
): void {
    postBrandToWorker(registration.active, brand);
    postBrandToWorker(registration.waiting, brand);
    postBrandToWorker(registration.installing, brand);
}

/**
 * Keep document meta in sync with Config (theme-color + iOS home-screen title).
 * index.html may be gitignored / brand-owned — runtime override avoids hardcoding.
 */
export function applyPwaDocumentBrand(
    doc: Document = document,
    brand: IPwaBrandConfig = getPwaBrandConfig()
): void {
    const themeMeta = doc.querySelector('meta[name="theme-color"]');
    if (themeMeta) {
        themeMeta.setAttribute('content', brand.themeColor);
    }

    let appleTitle = doc.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
        appleTitle = doc.createElement('meta');
        appleTitle.setAttribute('name', 'apple-mobile-web-app-title');
        doc.head.appendChild(appleTitle);
    }
    appleTitle.setAttribute('content', brand.siteName);
}

/**
 * Registers the root-scoped service worker required for installability.
 * Intentionally production-gated: CRA HMR + SW fighting causes confusing local bugs.
 */
export async function registerServiceWorker(options: IRegisterServiceWorkerOptions = {}): Promise<IRegisterServiceWorkerResult> {
    const {
        enabled = process.env.NODE_ENV === 'production',
        swUrl = PWA_SERVICE_WORKER_URL,
        registrationFn = typeof navigator !== 'undefined' && navigator.serviceWorker
            ? navigator.serviceWorker.register.bind(navigator.serviceWorker)
            : undefined,
        brand = getPwaBrandConfig(),
        onUpdateAvailable,
    } = options;

    if (!enabled) {
        return { registered: false, reason: 'non-production' as const };
    }

    if (!registrationFn) {
        return { registered: false, reason: 'unsupported' as const };
    }

    // A controller already present before this call means the page was
    // already running an earlier service worker version. A controllerchange
    // firing after that point is a live update taking over — as opposed to a
    // fresh visitor's very first activation, which must not trigger a prompt.
    const hadControllerBeforeRegister = typeof navigator !== 'undefined' && Boolean(navigator.serviceWorker?.controller);

    try {
        const registration = await registrationFn(swUrl, { scope: '/' });
        syncPwaBrandToServiceWorker(registration, brand);

        if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
            let updateNotified = false;

            navigator.serviceWorker.addEventListener('controllerchange', () => {
                if (navigator.serviceWorker.controller) {
                    postBrandToWorker(navigator.serviceWorker.controller, brand);
                }

                if (hadControllerBeforeRegister && !updateNotified) {
                    updateNotified = true;
                    onUpdateAvailable?.();
                }
            });
        }

        return { registered: true, reason: 'ok' as const, registration };
    } catch (error) {
        return {
            registered: false,
            reason: 'registration-failed' as const,
            error: error instanceof Error ? error : new Error(String(error)),
        };
    }
}

export async function unregisterServiceWorkers(
    getRegistrations: () => Promise<readonly ServiceWorkerRegistration[]> = () =>
        navigator.serviceWorker.getRegistrations()
): Promise<number> {
    const registrations = await getRegistrations();
    await Promise.all(registrations.map(async (registration) => registration.unregister()));
    return registrations.length;
}
